import { Box } from '@material-ui/core'
import { useState } from 'react'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Wrap } from 'shared/components/Base/Wrap'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Text } from 'shared/components/Text/Text'
import { darkGray, lightGray1, midGray } from 'shared/theme/colors'

import { formatReadableDate, formatReadableTime } from '../../../helpers/dateHelpers'
import { formatCurrency } from '../../../helpers/formatters'
import {
  DeductionConfig,
  DeductionConfigStatus,
  Repayment,
  VendorProgramPolicyType,
} from '../../../models/repayments'
import { Grid } from '../../Base/Grid'
import { useUserProvider } from '../../Context/UserContext'
import { PolicyActivityItem } from './PolicyActivityItem'

interface PolicyRefinanceDetailsProps {
  policy: Repayment
  config: DeductionConfig
  onCancelConfig: (id: string) => Promise<any>
}

export function PolicyRefinanceDetails({ policy, config, onCancelConfig }: PolicyRefinanceDetailsProps) {
  const { showBanner } = useUserProvider()
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const estimatedBillingPeriods =
    policy.repayment_details.total_amount_owed &&
    config.advance_periodic_amount &&
    !isNaN(Number(policy.repayment_details.total_amount_owed)) &&
    !isNaN(Number(config.advance_periodic_amount))
      ? Math.ceil(Number(policy.repayment_details.total_amount_owed) / Number(config.advance_periodic_amount))
      : '-'

  const onCancel = async () => {
    setIsLoading(true)
    try {
      await onCancelConfig(config.id)
      setIsLoading(false)
      setCancelDialogOpen(false)
    } catch (error: any) {
      setIsLoading(false)
      setCancelDialogOpen(false)
      if (
        error.response.data?.type === 'client_error' &&
        error.response.data.errors &&
        error.response.data.errors.every((e: any) => !!e.detail)
      )
        showBanner('error', {
          message: error.response.data.errors.map((e: { code: string; detail: string }) => e.detail),
        })
      else if (error.response.data.detail) showBanner('error', { message: error.response.data.detail })
      else showBanner('error', { message: `An error occurred: ${JSON.stringify(error.response.data)}` })
    }
  }

  return (
    <>
      <PolicyActivityItem
        config={config}
        cta={
          config.status === DeductionConfigStatus.SCHEDULED && (
            <Button
              variant="text"
              onClick={(e) => {
                e.stopPropagation()
                setCancelDialogOpen(true)
              }}
            >
              Cancel refinancing
            </Button>
          )
        }
      >
        <FlexBox isColumn alignItems="stretch" gap="8px">
          <Text variant="uppercaseLabel2" fontColor={midGray} align="left" mb={1}>
            REFINANCING DETAILS
          </Text>
          <Grid columns="300px 300px" gap="90px" smColumns="1fr" smGap="30px">
            <FlexBox isColumn gap="8px">
              <FlexBox justifyContent="space-between">
                <Text variant="header3" fontColor={lightGray1}>
                  Start date
                </Text>
                <FlexBox isColumn alignItems="flex-end">
                  <Text variant="header3" fontColor={darkGray}>
                    {formatReadableDate(config.date_scheduled)}
                  </Text>
                  <Text variant="header3" fontColor={darkGray}>
                    {formatReadableTime(config.date_scheduled)}
                  </Text>
                </FlexBox>
              </FlexBox>
              {policy.repayment_details.policy_type === VendorProgramPolicyType.SUBSCRIPTION && (
                <FlexBox justifyContent="space-between">
                  <Text variant="header3" fontColor={lightGray1}>
                    New weekly deduction
                  </Text>
                  <Text variant="header3" fontColor={darkGray}>
                    {formatCurrency(config.subscription_periodic_amount)}
                  </Text>
                </FlexBox>
              )}
              {policy.repayment_details.policy_type === VendorProgramPolicyType.ADVANCE && (
                <FlexBox justifyContent="space-between">
                  <Text variant="header3" fontColor={lightGray1}>
                    Estimated billing periods
                  </Text>
                  <Text variant="header3" fontColor={darkGray}>
                    {estimatedBillingPeriods}
                  </Text>
                </FlexBox>
              )}
            </FlexBox>
            <FlexBox isColumn gap="8px">
              {policy.repayment_details.policy_type === VendorProgramPolicyType.ADVANCE && (
                <>
                  <FlexBox justifyContent="space-between">
                    <Text variant="header3" fontColor={lightGray1}>
                      Total amount
                    </Text>
                    <Text variant="header3" fontColor={darkGray}>
                      {formatCurrency(policy.repayment_details.total_amount_owed)}
                    </Text>
                  </FlexBox>
                  <FlexBox justifyContent="space-between">
                    <Text variant="header3" fontColor={lightGray1}>
                      New weekly deduction
                    </Text>
                    <Text variant="header3" fontColor={darkGray}>
                      {formatCurrency(config.advance_periodic_amount)}
                    </Text>
                  </FlexBox>
                </>
              )}
            </FlexBox>
          </Grid>
        </FlexBox>
      </PolicyActivityItem>

      <SimpleDialog isOpen={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <Wrap width="380px" maxWidth="100%">
          <Text variant="header2" align="left">
            Cancel refinancing?
          </Text>

          <Box mt={9} />
          <Button margin="0 auto" variant="primary" size="large" onClick={onCancel} isLoading={isLoading}>
            Confirm
          </Button>
          <Box m={3} />
          <Button margin="0 auto" variant="text" onClick={() => setCancelDialogOpen(false)}>
            Don't cancel
          </Button>
        </Wrap>
      </SimpleDialog>
    </>
  )
}
